import { filterDriverRaces } from "../utils/raceUtils";

// A driver's result in one race on the chosen measure: fantasy points (finish + stage points) or the finish position
const valueOf = (race, feature) =>
  feature === "fantasy_points" ? (race.race_finish_points || 0) + (race.race_stage_points || 0) : race.race_pos;

// For finishing positions a smaller number is better
const lowerIsBetter = (feature) => feature === "race_pos";

// The races of the last n race weekends in a season's races
export const lastNRaces = (seasonRaces, n) => {
  const numbers = new Set([...new Set(seasonRaces.map((race) => race.race_number))].sort((a, b) => b - a).slice(0, n));
  return seasonRaces.filter((race) => numbers.has(race.race_number));
};

// How reliable a driver is over the given races, or null when there are none.
// floor is the worst result and ceiling the best, whichever way the measure runs.
export const summarizeDriver = (races, driver, feature) => {
  const driverRaces = filterDriverRaces(races, driver, false, false);
  const values = driverRaces.map((race) => valueOf(race, feature)).filter((value) => Number.isFinite(value));
  if (values.length === 0) return null;

  const average = values.reduce((sum, value) => sum + value, 0) / values.length;
  const variance = values.length > 1
    ? values.reduce((sum, value) => sum + (value - average) ** 2, 0) / (values.length - 1)
    : 0;
  const lowest = Math.min(...values);
  const highest = Math.max(...values);

  return {
    races: values.length,
    average,
    floor: lowerIsBetter(feature) ? highest : lowest,
    ceiling: lowerIsBetter(feature) ? lowest : highest,
    stdDev: Math.sqrt(variance),
    top10Rate: driverRaces.filter((race) => race.race_pos >= 1 && race.race_pos <= 10).length / driverRaces.length,
    dnfRate: driverRaces.filter((race) => race.status !== "finished").length / driverRaces.length,
  };
};

// Rank 1 = best in the group; drivers on equal averages share a rank
const rankByAverage = (averages, feature) => {
  const ranks = {};
  Object.entries(averages).forEach(([driver, average]) => {
    if (average === null) return;
    ranks[driver] = 1 + Object.values(averages).filter(
      (other) => other !== null && (lowerIsBetter(feature) ? other < average : other > average)
    ).length;
  });
  return ranks;
};

// Ranks every driver in the group on each set of races ({ track: races, similar: races, form: races }) and orders
// them by the mean of the ranks they have. A driver with no data on a measure is not ranked on it, and one with
// no data at all goes last.
export const rankGroup = (drivers, racesByMeasure, feature) => {
  const measures = Object.keys(racesByMeasure);
  const averages = {};
  const ranks = {};
  measures.forEach((measure) => {
    averages[measure] = Object.fromEntries(
      drivers.map((driver) => [driver, summarizeDriver(racesByMeasure[measure], driver, feature)?.average ?? null])
    );
    ranks[measure] = rankByAverage(averages[measure], feature);
  });

  return drivers
    .map((driver) => {
      const driverRanks = measures.map((measure) => ranks[measure][driver]).filter((rank) => rank !== undefined);
      return {
        driver,
        averages: Object.fromEntries(measures.map((measure) => [measure, averages[measure][driver]])),
        ranks: Object.fromEntries(measures.map((measure) => [measure, ranks[measure][driver] ?? null])),
        score: driverRanks.length > 0 ? driverRanks.reduce((sum, rank) => sum + rank, 0) / driverRanks.length : null,
        measuresUsed: driverRanks.length,
      };
    })
    .sort((a, b) => {
      if (a.score === null || b.score === null) return (a.score === null) - (b.score === null);
      return a.score - b.score || b.measuresUsed - a.measuresUsed || a.driver.localeCompare(b.driver);
    });
};
